/**
 * Letter Strokes Guide Data for Indonesian Early Reading (Ayo Baca - Menulis Huruf)
 * Provides sequential stroke paths, numbered start points, and audio directions
 * for both Uppercase (Huruf Besar) and Lowercase (huruf kecil) A-Z.
 */

export interface StrokePoint {
  x: number; // 0 - 100 (% of bounding box width)
  y: number; // 0 - 100 (% of bounding box height)
}

export interface LetterStroke {
  order: number; // 1, 2, 3...
  label: string; // "1", "2", "3"
  instruction: string; // Encouraging child direction
  points: StrokePoint[];
}

export interface LetterWritingData {
  letter: string;
  isUppercase: boolean;
  name: string;
  exampleWord: string;
  emoji: string;
  color: string;
  phonics: string;
  strokes: LetterStroke[];
}

export const UPPERCASE_LETTER_STROKES: Record<string, LetterStroke[]> = {
  A: [
    { order: 1, label: '1', instruction: 'Tarik garis miring dari atas ke bawah kiri', points: [{ x: 50, y: 15 }, { x: 35, y: 50 }, { x: 22, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis miring dari atas ke bawah kanan', points: [{ x: 50, y: 15 }, { x: 65, y: 50 }, { x: 78, y: 85 }] },
    { order: 3, label: '3', instruction: 'Tarik garis tidur di tengah dari kiri ke kanan', points: [{ x: 32, y: 58 }, { x: 50, y: 58 }, { x: 68, y: 58 }] },
  ],
  B: [
    { order: 1, label: '1', instruction: 'Tarik garis lurus tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat perut melengkung atas ke kanan', points: [{ x: 28, y: 15 }, { x: 55, y: 15 }, { x: 68, y: 32 }, { x: 55, y: 50 }, { x: 28, y: 50 }] },
    { order: 3, label: '3', instruction: 'Buat perut melengkung bawah ke kanan', points: [{ x: 28, y: 50 }, { x: 60, y: 50 }, { x: 72, y: 67 }, { x: 60, y: 85 }, { x: 28, y: 85 }] },
  ],
  C: [
    { order: 1, label: '1', instruction: 'Buat lengkungan besar memutar ke kiri seperti bulan sabit', points: [{ x: 75, y: 25 }, { x: 48, y: 15 }, { x: 22, y: 50 }, { x: 48, y: 85 }, { x: 75, y: 75 }] },
  ],
  D: [
    { order: 1, label: '1', instruction: 'Tarik garis lurus tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat perut melengkung besar ke kanan', points: [{ x: 28, y: 15 }, { x: 60, y: 18 }, { x: 75, y: 50 }, { x: 60, y: 82 }, { x: 28, y: 85 }] },
  ],
  E: [
    { order: 1, label: '1', instruction: 'Tarik garis lurus tegak ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis tidur di atas ke kanan', points: [{ x: 28, y: 15 }, { x: 50, y: 15 }, { x: 72, y: 15 }] },
    { order: 3, label: '3', instruction: 'Tarik garis tidur di tengah ke kanan', points: [{ x: 28, y: 50 }, { x: 45, y: 50 }, { x: 65, y: 50 }] },
    { order: 4, label: '4', instruction: 'Tarik garis tidur di bawah ke kanan', points: [{ x: 28, y: 85 }, { x: 50, y: 85 }, { x: 72, y: 85 }] },
  ],
  F: [
    { order: 1, label: '1', instruction: 'Tarik garis lurus tegak ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis tidur di atas ke kanan', points: [{ x: 28, y: 15 }, { x: 50, y: 15 }, { x: 72, y: 15 }] },
    { order: 3, label: '3', instruction: 'Tarik garis tidur di tengah ke kanan', points: [{ x: 28, y: 50 }, { x: 45, y: 50 }, { x: 65, y: 50 }] },
  ],
  G: [
    { order: 1, label: '1', instruction: 'Buat lengkungan besar memutar ke kiri', points: [{ x: 75, y: 25 }, { x: 48, y: 15 }, { x: 22, y: 50 }, { x: 48, y: 85 }, { x: 75, y: 75 }, { x: 75, y: 55 }] },
    { order: 2, label: '2', instruction: 'Tarik garis tidur masuk ke kiri', points: [{ x: 75, y: 55 }, { x: 62, y: 55 }, { x: 50, y: 55 }] },
  ],
  H: [
    { order: 1, label: '1', instruction: 'Tarik garis tegak di kiri ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis tegak di kanan ke bawah', points: [{ x: 72, y: 15 }, { x: 72, y: 50 }, { x: 72, y: 85 }] },
    { order: 3, label: '3', instruction: 'Tarik jembatan di tengah dari kiri ke kanan', points: [{ x: 28, y: 50 }, { x: 50, y: 50 }, { x: 72, y: 50 }] },
  ],
  I: [
    { order: 1, label: '1', instruction: 'Tarik garis tegak dari atas ke bawah', points: [{ x: 50, y: 15 }, { x: 50, y: 50 }, { x: 50, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik topi tidur di atas', points: [{ x: 30, y: 15 }, { x: 50, y: 15 }, { x: 70, y: 15 }] },
    { order: 3, label: '3', instruction: 'Tarik alas tidur di bawah', points: [{ x: 30, y: 85 }, { x: 50, y: 85 }, { x: 70, y: 85 }] },
  ],
  J: [
    { order: 1, label: '1', instruction: 'Tarik topi tidur di atas', points: [{ x: 32, y: 15 }, { x: 50, y: 15 }, { x: 72, y: 15 }] },
    { order: 2, label: '2', instruction: 'Tarik garis tegak lalu melengkung seperti tongkat kakek', points: [{ x: 60, y: 15 }, { x: 60, y: 68 }, { x: 45, y: 85 }, { x: 28, y: 76 }] },
  ],
  K: [
    { order: 1, label: '1', instruction: 'Tarik garis tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis miring dari kanan atas ke tengah', points: [{ x: 72, y: 18 }, { x: 50, y: 35 }, { x: 30, y: 50 }] },
    { order: 3, label: '3', instruction: 'Tarik kaki miring dari tengah ke kanan bawah', points: [{ x: 34, y: 50 }, { x: 55, y: 68 }, { x: 75, y: 85 }] },
  ],
  L: [
    { order: 1, label: '1', instruction: 'Tarik garis tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis tidur ke kanan bawah', points: [{ x: 28, y: 85 }, { x: 50, y: 85 }, { x: 72, y: 85 }] },
  ],
  M: [
    { order: 1, label: '1', instruction: 'Tarik garis tegak di kiri dari bawah ke atas', points: [{ x: 22, y: 85 }, { x: 22, y: 50 }, { x: 22, y: 15 }] },
    { order: 2, label: '2', instruction: 'Tarik miring ke tengah bawah', points: [{ x: 22, y: 15 }, { x: 36, y: 38 }, { x: 50, y: 62 }] },
    { order: 3, label: '3', instruction: 'Tarik miring ke kanan atas', points: [{ x: 50, y: 62 }, { x: 64, y: 38 }, { x: 78, y: 15 }] },
    { order: 4, label: '4', instruction: 'Tarik garis tegak di kanan ke bawah', points: [{ x: 78, y: 15 }, { x: 78, y: 50 }, { x: 78, y: 85 }] },
  ],
  N: [
    { order: 1, label: '1', instruction: 'Tarik garis tegak kiri dari bawah ke atas', points: [{ x: 25, y: 85 }, { x: 25, y: 50 }, { x: 25, y: 15 }] },
    { order: 2, label: '2', instruction: 'Tarik garis miring ke kanan bawah', points: [{ x: 25, y: 15 }, { x: 50, y: 50 }, { x: 75, y: 85 }] },
    { order: 3, label: '3', instruction: 'Tarik garis tegak kanan ke atas', points: [{ x: 75, y: 85 }, { x: 75, y: 50 }, { x: 75, y: 15 }] },
  ],
  O: [
    { order: 1, label: '1', instruction: 'Buat lingkaran bulat penuh memutar ke kiri', points: [{ x: 50, y: 15 }, { x: 22, y: 40 }, { x: 22, y: 60 }, { x: 50, y: 85 }, { x: 78, y: 60 }, { x: 78, y: 40 }, { x: 50, y: 15 }] },
  ],
  P: [
    { order: 1, label: '1', instruction: 'Tarik garis tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat kepala melengkung di kanan atas', points: [{ x: 28, y: 15 }, { x: 58, y: 15 }, { x: 72, y: 35 }, { x: 58, y: 55 }, { x: 28, y: 55 }] },
  ],
  Q: [
    { order: 1, label: '1', instruction: 'Buat lingkaran bulat penuh memutar ke kiri', points: [{ x: 50, y: 15 }, { x: 22, y: 40 }, { x: 22, y: 60 }, { x: 50, y: 85 }, { x: 78, y: 60 }, { x: 78, y: 40 }, { x: 50, y: 15 }] },
    { order: 2, label: '2', instruction: 'Tarik ekor miring kecil di kanan bawah', points: [{ x: 56, y: 68 }, { x: 70, y: 80 }, { x: 82, y: 90 }] },
  ],
  R: [
    { order: 1, label: '1', instruction: 'Tarik garis tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat kepala melengkung di kanan atas', points: [{ x: 28, y: 15 }, { x: 58, y: 15 }, { x: 72, y: 32 }, { x: 58, y: 50 }, { x: 28, y: 50 }] },
    { order: 3, label: '3', instruction: 'Tarik kaki miring ke kanan bawah', points: [{ x: 45, y: 50 }, { x: 60, y: 68 }, { x: 75, y: 85 }] },
  ],
  S: [
    { order: 1, label: '1', instruction: 'Buat lengkungan berliku seperti ular', points: [{ x: 75, y: 25 }, { x: 50, y: 15 }, { x: 28, y: 32 }, { x: 50, y: 50 }, { x: 72, y: 68 }, { x: 50, y: 85 }, { x: 25, y: 75 }] },
  ],
  T: [
    { order: 1, label: '1', instruction: 'Tarik topi tidur mendatar di atas', points: [{ x: 20, y: 15 }, { x: 50, y: 15 }, { x: 80, y: 15 }] },
    { order: 2, label: '2', instruction: 'Tarik tiang tegak dari tengah ke bawah', points: [{ x: 50, y: 15 }, { x: 50, y: 50 }, { x: 50, y: 85 }] },
  ],
  U: [
    { order: 1, label: '1', instruction: 'Tarik ke bawah, melengkung di dasar, lalu naik ke atas seperti mangkuk', points: [{ x: 25, y: 15 }, { x: 25, y: 62 }, { x: 50, y: 85 }, { x: 75, y: 62 }, { x: 75, y: 15 }] },
  ],
  V: [
    { order: 1, label: '1', instruction: 'Tarik miring dari kiri atas ke lancip bawah', points: [{ x: 22, y: 15 }, { x: 36, y: 50 }, { x: 50, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik miring dari lancip bawah ke kanan atas', points: [{ x: 50, y: 85 }, { x: 64, y: 50 }, { x: 78, y: 15 }] },
  ],
  W: [
    { order: 1, label: '1', instruction: 'Tarik miring ke bawah pertama', points: [{ x: 18, y: 15 }, { x: 25, y: 50 }, { x: 32, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik miring naik ke tengah', points: [{ x: 32, y: 85 }, { x: 41, y: 65 }, { x: 50, y: 45 }] },
    { order: 3, label: '3', instruction: 'Tarik miring turun lagi ke bawah', points: [{ x: 50, y: 45 }, { x: 59, y: 65 }, { x: 68, y: 85 }] },
    { order: 4, label: '4', instruction: 'Tarik miring naik ke kanan atas', points: [{ x: 68, y: 85 }, { x: 75, y: 50 }, { x: 82, y: 15 }] },
  ],
  X: [
    { order: 1, label: '1', instruction: 'Tarik garis silang dari kiri atas ke kanan bawah', points: [{ x: 25, y: 15 }, { x: 50, y: 50 }, { x: 75, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis silang dari kanan atas ke kiri bawah', points: [{ x: 75, y: 15 }, { x: 50, y: 50 }, { x: 25, y: 85 }] },
  ],
  Y: [
    { order: 1, label: '1', instruction: 'Tarik miring kiri ke titik tengah', points: [{ x: 25, y: 15 }, { x: 38, y: 34 }, { x: 50, y: 52 }] },
    { order: 2, label: '2', instruction: 'Tarik miring kanan ke titik tengah', points: [{ x: 75, y: 15 }, { x: 62, y: 34 }, { x: 50, y: 52 }] },
    { order: 3, label: '3', instruction: 'Tarik tiang tegak dari tengah ke bawah', points: [{ x: 50, y: 52 }, { x: 50, y: 68 }, { x: 50, y: 85 }] },
  ],
  Z: [
    { order: 1, label: '1', instruction: 'Tarik garis tidur di atas ke kanan', points: [{ x: 25, y: 15 }, { x: 50, y: 15 }, { x: 75, y: 15 }] },
    { order: 2, label: '2', instruction: 'Tarik garis miring dari kanan atas ke kiri bawah', points: [{ x: 75, y: 15 }, { x: 50, y: 50 }, { x: 25, y: 85 }] },
    { order: 3, label: '3', instruction: 'Tarik garis tidur di bawah ke kanan', points: [{ x: 25, y: 85 }, { x: 50, y: 85 }, { x: 75, y: 85 }] },
  ],
};

export const LOWERCASE_LETTER_STROKES: Record<string, LetterStroke[]> = {
  a: [
    { order: 1, label: '1', instruction: 'Buat perut bulat memutar ke kiri', points: [{ x: 65, y: 48 }, { x: 45, y: 38 }, { x: 26, y: 58 }, { x: 45, y: 80 }, { x: 65, y: 78 }] },
    { order: 2, label: '2', instruction: 'Tarik tiang lurus ke bawah di kanan', points: [{ x: 65, y: 38 }, { x: 65, y: 62 }, { x: 65, y: 85 }] },
  ],
  b: [
    { order: 1, label: '1', instruction: 'Tarik garis tinggi tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat perut bulat melengkung di kanan', points: [{ x: 28, y: 52 }, { x: 55, y: 40 }, { x: 74, y: 62 }, { x: 55, y: 85 }, { x: 28, y: 82 }] },
  ],
  c: [
    { order: 1, label: '1', instruction: 'Buat lengkungan memutar ke kiri seperti bulan kecil', points: [{ x: 72, y: 45 }, { x: 50, y: 38 }, { x: 28, y: 62 }, { x: 50, y: 85 }, { x: 72, y: 78 }] },
  ],
  d: [
    { order: 1, label: '1', instruction: 'Buat perut bulat memutar di kiri', points: [{ x: 68, y: 52 }, { x: 45, y: 40 }, { x: 26, y: 62 }, { x: 45, y: 85 }, { x: 68, y: 80 }] },
    { order: 2, label: '2', instruction: 'Tarik tiang tinggi tegak dari atas ke bawah di kanan', points: [{ x: 68, y: 15 }, { x: 68, y: 50 }, { x: 68, y: 85 }] },
  ],
  e: [
    { order: 1, label: '1', instruction: 'Tarik garis tidur di tengah ke kanan', points: [{ x: 28, y: 60 }, { x: 50, y: 60 }, { x: 72, y: 60 }] },
    { order: 2, label: '2', instruction: 'Melengkung memutar ke atas lalu ke bawah', points: [{ x: 72, y: 60 }, { x: 50, y: 38 }, { x: 28, y: 58 }, { x: 50, y: 85 }, { x: 72, y: 78 }] },
  ],
  f: [
    { order: 1, label: '1', instruction: 'Tarik lengkung tongkat dari atas lalu lurus ke bawah', points: [{ x: 65, y: 22 }, { x: 50, y: 15 }, { x: 40, y: 30 }, { x: 40, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis sabuk di tengah dari kiri ke kanan', points: [{ x: 25, y: 48 }, { x: 40, y: 48 }, { x: 60, y: 48 }] },
  ],
  g: [
    { order: 1, label: '1', instruction: 'Buat perut bulat di atas memutar ke kiri', points: [{ x: 68, y: 50 }, { x: 45, y: 38 }, { x: 25, y: 60 }, { x: 45, y: 82 }, { x: 68, y: 78 }] },
    { order: 2, label: '2', instruction: 'Tarik tiang ke bawah lalu melengkung ke kiri seperti kail', points: [{ x: 68, y: 38 }, { x: 68, y: 82 }, { x: 52, y: 96 }, { x: 35, y: 90 }] },
  ],
  h: [
    { order: 1, label: '1', instruction: 'Tarik tiang tinggi tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat bukit melengkung di kanan ke bawah', points: [{ x: 28, y: 55 }, { x: 50, y: 40 }, { x: 72, y: 55 }, { x: 72, y: 85 }] },
  ],
  i: [
    { order: 1, label: '1', instruction: 'Tarik tiang pendek tegak ke bawah', points: [{ x: 50, y: 42 }, { x: 50, y: 64 }, { x: 50, y: 85 }] },
    { order: 2, label: '2', instruction: 'Beri titik kecil di atas tiang', points: [{ x: 50, y: 24 }, { x: 50, y: 26 }] },
  ],
  j: [
    { order: 1, label: '1', instruction: 'Tarik tiang ke bawah lalu melengkung ke kiri', points: [{ x: 55, y: 42 }, { x: 55, y: 85 }, { x: 42, y: 96 }, { x: 28, y: 88 }] },
    { order: 2, label: '2', instruction: 'Beri titik kecil di atas', points: [{ x: 55, y: 24 }, { x: 55, y: 26 }] },
  ],
  k: [
    { order: 1, label: '1', instruction: 'Tarik tiang tinggi tegak dari atas ke bawah', points: [{ x: 28, y: 15 }, { x: 28, y: 50 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik miring dari kanan ke tiang tengah', points: [{ x: 68, y: 42 }, { x: 50, y: 52 }, { x: 30, y: 62 }] },
    { order: 3, label: '3', instruction: 'Tarik kaki miring ke kanan bawah', points: [{ x: 34, y: 60 }, { x: 52, y: 72 }, { x: 70, y: 85 }] },
  ],
  l: [
    { order: 1, label: '1', instruction: 'Tarik satu tiang lurus tinggi dari atas ke bawah', points: [{ x: 50, y: 15 }, { x: 50, y: 50 }, { x: 50, y: 85 }] },
  ],
  m: [
    { order: 1, label: '1', instruction: 'Tarik tiang pendek di kiri ke bawah', points: [{ x: 22, y: 42 }, { x: 22, y: 64 }, { x: 22, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat bukit melengkung pertama ke bawah', points: [{ x: 22, y: 55 }, { x: 36, y: 40 }, { x: 50, y: 55 }, { x: 50, y: 85 }] },
    { order: 3, label: '3', instruction: 'Buat bukit melengkung kedua ke bawah', points: [{ x: 50, y: 55 }, { x: 64, y: 40 }, { x: 78, y: 55 }, { x: 78, y: 85 }] },
  ],
  n: [
    { order: 1, label: '1', instruction: 'Tarik tiang pendek di kiri ke bawah', points: [{ x: 28, y: 42 }, { x: 28, y: 64 }, { x: 28, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat satu bukit melengkung ke kanan bawah', points: [{ x: 28, y: 55 }, { x: 50, y: 40 }, { x: 72, y: 55 }, { x: 72, y: 85 }] },
  ],
  o: [
    { order: 1, label: '1', instruction: 'Buat lingkaran kecil memutar ke kiri', points: [{ x: 50, y: 40 }, { x: 28, y: 55 }, { x: 28, y: 70 }, { x: 50, y: 85 }, { x: 72, y: 70 }, { x: 72, y: 55 }, { x: 50, y: 40 }] },
  ],
  p: [
    { order: 1, label: '1', instruction: 'Tarik tiang lurus ke bawah hingga menembus garis', points: [{ x: 28, y: 42 }, { x: 28, y: 70 }, { x: 28, y: 98 }] },
    { order: 2, label: '2', instruction: 'Buat kepala bulat di kanan tiang', points: [{ x: 28, y: 55 }, { x: 55, y: 40 }, { x: 74, y: 62 }, { x: 55, y: 85 }, { x: 28, y: 80 }] },
  ],
  q: [
    { order: 1, label: '1', instruction: 'Buat perut bulat di kiri tiang', points: [{ x: 72, y: 55 }, { x: 45, y: 40 }, { x: 26, y: 62 }, { x: 45, y: 85 }, { x: 72, y: 80 }] },
    { order: 2, label: '2', instruction: 'Tarik tiang tegak ke bawah menembus garis', points: [{ x: 72, y: 42 }, { x: 72, y: 70 }, { x: 72, y: 98 }] },
  ],
  r: [
    { order: 1, label: '1', instruction: 'Tarik tiang pendek ke bawah', points: [{ x: 35, y: 42 }, { x: 35, y: 64 }, { x: 35, y: 85 }] },
    { order: 2, label: '2', instruction: 'Buat ranting melengkung kecil ke kanan', points: [{ x: 35, y: 58 }, { x: 52, y: 42 }, { x: 70, y: 48 }] },
  ],
  s: [
    { order: 1, label: '1', instruction: 'Buat lengkungan berliku kecil seperti anak ular', points: [{ x: 70, y: 48 }, { x: 50, y: 40 }, { x: 32, y: 50 }, { x: 50, y: 62 }, { x: 68, y: 75 }, { x: 50, y: 85 }, { x: 30, y: 80 }] },
  ],
  t: [
    { order: 1, label: '1', instruction: 'Tarik tiang melengkung sedikit di bawah', points: [{ x: 48, y: 22 }, { x: 48, y: 75 }, { x: 62, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis sabuk di tengah dari kiri ke kanan', points: [{ x: 28, y: 45 }, { x: 48, y: 45 }, { x: 68, y: 45 }] },
  ],
  u: [
    { order: 1, label: '1', instruction: 'Tarik ke bawah lalu melengkung ke atas seperti cangkir', points: [{ x: 28, y: 42 }, { x: 28, y: 70 }, { x: 48, y: 85 }, { x: 68, y: 70 }, { x: 68, y: 42 }] },
    { order: 2, label: '2', instruction: 'Tarik tiang pendek di kanan ke bawah', points: [{ x: 68, y: 42 }, { x: 68, y: 64 }, { x: 68, y: 85 }] },
  ],
  v: [
    { order: 1, label: '1', instruction: 'Tarik miring ke bawah lancip', points: [{ x: 28, y: 42 }, { x: 39, y: 64 }, { x: 50, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik miring naik ke kanan atas', points: [{ x: 50, y: 85 }, { x: 61, y: 64 }, { x: 72, y: 42 }] },
  ],
  w: [
    { order: 1, label: '1', instruction: 'Tarik miring ke bawah pertama', points: [{ x: 20, y: 42 }, { x: 27, y: 64 }, { x: 34, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik miring naik ke tengah', points: [{ x: 34, y: 85 }, { x: 42, y: 70 }, { x: 50, y: 55 }] },
    { order: 3, label: '3', instruction: 'Tarik miring turun lagi', points: [{ x: 50, y: 55 }, { x: 58, y: 70 }, { x: 66, y: 85 }] },
    { order: 4, label: '4', instruction: 'Tarik miring naik ke kanan', points: [{ x: 66, y: 85 }, { x: 73, y: 64 }, { x: 80, y: 42 }] },
  ],
  x: [
    { order: 1, label: '1', instruction: 'Tarik garis silang dari kiri ke kanan bawah', points: [{ x: 30, y: 42 }, { x: 50, y: 64 }, { x: 70, y: 85 }] },
    { order: 2, label: '2', instruction: 'Tarik garis silang dari kanan ke kiri bawah', points: [{ x: 70, y: 42 }, { x: 50, y: 64 }, { x: 30, y: 85 }] },
  ],
  y: [
    { order: 1, label: '1', instruction: 'Tarik miring pendek ke tengah', points: [{ x: 28, y: 42 }, { x: 39, y: 57 }, { x: 50, y: 72 }] },
    { order: 2, label: '2', instruction: 'Tarik miring panjang menembus ke kiri bawah', points: [{ x: 72, y: 42 }, { x: 52, y: 70 }, { x: 32, y: 98 }] },
  ],
  z: [
    { order: 1, label: '1', instruction: 'Tarik garis tidur di atas ke kanan', points: [{ x: 30, y: 42 }, { x: 50, y: 42 }, { x: 70, y: 42 }] },
    { order: 2, label: '2', instruction: 'Tarik miring ke kiri bawah', points: [{ x: 70, y: 42 }, { x: 50, y: 64 }, { x: 30, y: 85 }] },
    { order: 3, label: '3', instruction: 'Tarik garis tidur di bawah ke kanan', points: [{ x: 30, y: 85 }, { x: 50, y: 85 }, { x: 70, y: 85 }] },
  ],
};
